const AUTH_RETURN_LOCATION_KEY = "tra_auth_return_location";

/**
 * @param {Pick<Location, "pathname" | "search" | "hash"> | string | null | undefined} loc
 * @returns {{ pathname: string; search: string; hash: string } | null}
 */
export function normalizeAuthReturnLocation(loc) {
  if (!loc) return null;

  if (typeof loc === "string") {
    const raw = loc.trim();
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    try {
      const url = new URL(raw, "https://therallyally.local");
      return sanitizeReturnParts(url.pathname, url.search, url.hash);
    } catch {
      return null;
    }
  }

  const pathname = String(loc.pathname || "").trim() || "/";
  if (!pathname.startsWith("/") || pathname.startsWith("//")) return null;
  return sanitizeReturnParts(pathname, loc.search || "", loc.hash || "");
}

/**
 * @param {string} pathname
 * @param {string} search
 * @param {string} hash
 */
function sanitizeReturnParts(pathname, search, hash) {
  if (pathname === "/sign-in" || pathname.startsWith("/sign-in/")) {
    return null;
  }
  return {
    pathname,
    search: search && String(search).startsWith("?") ? String(search) : search ? `?${search}` : "",
    hash: hash && String(hash).startsWith("#") ? String(hash) : hash ? `#${hash}` : "",
  };
}

/**
 * Persist where to return after sign-in (survives refresh on /sign-in).
 * @param {Pick<Location, "pathname" | "search" | "hash"> | string | null | undefined} loc
 */
export function writeAuthReturnLocation(loc) {
  const normalized = normalizeAuthReturnLocation(loc);
  try {
    if (!normalized) {
      sessionStorage.removeItem(AUTH_RETURN_LOCATION_KEY);
      return null;
    }
    sessionStorage.setItem(AUTH_RETURN_LOCATION_KEY, JSON.stringify(normalized));
    return normalized;
  } catch {
    return normalized;
  }
}

export function readAuthReturnLocation() {
  try {
    const raw = sessionStorage.getItem(AUTH_RETURN_LOCATION_KEY);
    if (!raw) return null;
    return normalizeAuthReturnLocation(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearAuthReturnLocation() {
  try {
    sessionStorage.removeItem(AUTH_RETURN_LOCATION_KEY);
  } catch {
    /* private mode */
  }
}

/**
 * @param {{ pathname: string; search?: string; hash?: string }} loc
 * @returns {string | { pathname: string; search?: string; hash?: string }}
 */
export function formatAuthReturnTarget(loc) {
  if (!loc?.pathname) return "/";
  if (!loc.search && !loc.hash) return loc.pathname;
  return {
    pathname: loc.pathname,
    ...(loc.search ? { search: loc.search } : {}),
    ...(loc.hash ? { hash: loc.hash } : {}),
  };
}
